import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { EmptyState } from '@application/flexible_component/flexible_identifier/flexible_identifier_flexible_recent_visits.component';
import { withTranslation } from '@application/i18n/with_translation';
import { DateService } from '@application/time/date.service';
import { ActionCard, BadgeText, Flex, Spacer } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Shimmer } from '@component/new_shimmer.component';
import { Title } from '@component/title.component';
import { TopBar } from '@component/top_bar.component';
import { capitalizeFirstLetter } from '@module/capitalize_first_letter';
import { useResolve } from '@visiba/instantiation';
import { Dayjs } from 'dayjs';
import { ViewBookingAnalyticActions } from '../../hooks/use_booking';
import { Location, useScreenLocation } from './use_location';

export function ScreenLocation() {
	const screenLocation = useScreenLocation();
	const analytic = useAnalyticContext();
	const dateService = useResolve(DateService);

	function getRelativeDateLabel(date: Dayjs): string {
		if (dateService.isToday(date)) {
			return withTranslation('Common.Word.Today');
		}
		if (dateService.isTomorrow(date)) {
			return withTranslation('Common.Word.Tomorrow');
		}
		if (dateService.isThisYear(date)) {
			return date.format('dddd D MMMM');
		}
		return date.format('LL');
	}

	function formatNextAvailableTime(date: Dayjs): string {
		const dateFormat = getRelativeDateLabel(date);

		return withTranslation('View.Booking.ScreenLocation.NextAvailableTime', {
			date: capitalizeFirstLetter(dateFormat),
			time: date.format('HH:mm'),
		});
	}

	function handleLocation(location: Location) {
		return () => {
			analytic.trackEvent(ViewBookingAnalyticActions.SelectLocation, `${location.id}`);
			screenLocation.selectLocation(location);
		};
	}

	const renderLocations = () => {
		if (screenLocation.locations.status === 'pending') {
			return <Skeleton />;
		}
		if (!screenLocation.locations.value) {
			return null;
		}
		if (!screenLocation.locations.value.length) {
			return <EmptyState>{withTranslation('View.Booking.ScreenLocation.NoAvailableTimes')}</EmptyState>;
		}
		return (
			<>
				{screenLocation.locations.value
					.filter((location) => location.nextAvailableTime)
					.map((location) => (
						<ActionCard.Root
							key={location.id}
							icon={location.type === 'digital' ? 'video' : 'intoBuilding'}
							onClick={handleLocation(location)}
						>
							<ActionCard.Title>{location.name}</ActionCard.Title>
							<ActionCard.Meta>{location.meta}</ActionCard.Meta>
							{location.nextAvailableTime ? (
								<span>
									<BadgeText variant='brandLight'>{formatNextAvailableTime(location.nextAvailableTime)}</BadgeText>
								</span>
							) : null}
						</ActionCard.Root>
					))}
			</>
		);
	};

	return (
		<>
			<TopBar.Content goBack />
			<Title
				title={withTranslation('View.Booking.ScreenLocation.Title')}
				titleSize='small'
				description={withTranslation('View.Booking.ScreenLocation.Description')}
			/>

			<Spacer vertical={tokens.spacing.x2_5} />

			<Flex gap={tokens.spacing.x1}>{renderLocations()}</Flex>
		</>
	);
}

function Skeleton() {
	return (
		<>
			<Shimmer height={80} width='100%' />
			<Shimmer height={80} width='100%' />
			<Shimmer height={80} width='100%' />
		</>
	);
}
